import React, { useEffect, useState } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { Stack } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import type { SelectedType } from '@atlaskit/tabs/types';
import { token } from '@atlaskit/tokens';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { PRIORITY_SCHEME_MODAL_SUBMIT_BUTTON_SELECTOR } from '../../common/constants';
import {
	DEFAULT,
	PRIORITY_SCHEME_MODAL_DETAILS_TAB_INDEX,
	SAVING,
} from '../../common/ui/constants';
import {
	type MappingsDiffs,
	type CreatePriorityScheme,
	type FlagData,
	type PrioritySchemeFormState,
	type PrioritySchemeModalWrapperProps,
	ModalOnCompleteStatus,
} from '../../common/ui/types';
import { usePrioritySchemeDetailsController } from '../../controllers/priority-scheme-details';
import { usePriorityMappingsController } from '../../controllers/priority-scheme-mappings';
import { usePrioritySchemeMappingsOut } from '../../controllers/priority-scheme-mappings-out';
import { useProjectPickerController } from '../../controllers/project-picker-controller';
import { useProjectPickerService } from '../../services/project-picker';
import { PrioritySchemeDetailsForm, PrioritySchemeModalWithAnalytics } from './main';
import { MappingModalView } from './mapping-modal-view';
import messages from './messages';
import { usePrioritySchemeShowFlags } from './modal-flags';
import {
	type Fields,
	calculatePrioritiesDiff,
	calculateProjectsDiff,
	fireSaveAnalyticsEvent,
	handleApiValidationErrors,
	savePrioritySchemeRequest,
	mapPriorityMappingsToRequestMappings,
} from './utils';

const emptyCreateSchemeData: CreatePriorityScheme = {
	id: undefined,
	name: '',
	description: '',
	isDefault: false,
	priorities: [],
	projects: {
		total: 0,
		values: [],
	},
};

const emptyMappingDiff = {
	prioritiesDiff: {
		add: [],
		remove: [],
	},
	projectsDiff: {
		add: [],
		remove: [],
	},
};

const learnMoreURL =
	'https://support.atlassian.com/jira-cloud-administration/docs/configure-priorities-for-projects';

export const AddOrEditPrioritySchemeModal = (
	modalWrapperProps: PrioritySchemeModalWrapperProps,
) => {
	const { formatMessage } = useIntl();
	const { initialPriorityScheme, onComplete, startingTab } = modalWrapperProps;

	const [formError, setFormError] = useState<Fields>({
		name: '',
		description: '',
		priorityIds: '',
		projectIds: '',
	});

	const [requiresRemovedProjectsMapping, setRequiresRemovedProjectsMapping] =
		useState<boolean>(false);

	const { defaultSchemeId, defaultSchemePriorities, isDefaultSchemeError, isDefaultSchemeLoading } =
		usePrioritySchemeMappingsOut({
			requiresRemovedProjectsMapping,
		});

	const [currentSchemeMappingDiffs, setCurrentSchemeMappingDiffs] =
		useState<MappingsDiffs>(emptyMappingDiff);

	const [defaultSchemeMappingDiffs, setDefaultSchemeMappingDiffs] =
		useState<MappingsDiffs>(emptyMappingDiff);

	const [prioritySchemeDetailsFormTab, setPrioritySchemeDetailsFormTab] = useState<SelectedType>(
		startingTab || PRIORITY_SCHEME_MODAL_DETAILS_TAB_INDEX,
	);

	const [prioritySchemeModalState, setPrioritySchemeModalState] =
		useState<PrioritySchemeFormState>(DEFAULT);

	/**
	 * Add project mappings refers to mapping to the default scheme, removed project mappings is the second case
	 * where the projected removed from the 'AddedProjectsMapping' case are added the 'RemovedProjectsMapping'
	 */
	const [modalStep, setModalStep] = useState<
		'FormView' | 'AddedProjectsMapping' | 'RemovedProjectsMapping'
	>('FormView');

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { showSuccessFlag, showAsyncSuccessFlag, showUnexpectedErrorFlag } =
		usePrioritySchemeShowFlags();

	const initialScheme = initialPriorityScheme || emptyCreateSchemeData;
	const isCreatingNewScheme = !initialPriorityScheme;

	const schemeDetailsProps = usePrioritySchemeDetailsController(initialScheme, setFormError);
	const { priorityScheme, triggerSchemeDetailsValidation } = schemeDetailsProps;

	const projectPickerProps = useProjectPickerController(useProjectPickerService(initialScheme));
	const { pendingChanges } = projectPickerProps;

	const projectsDiff = calculateProjectsDiff(pendingChanges);

	const prioritiesDiff = calculatePrioritiesDiff(
		initialScheme.priorities,
		priorityScheme.priorities,
	);

	useEffect(() => {
		const newProjectDiffs = calculateProjectsDiff(pendingChanges);
		setCurrentSchemeMappingDiffs((prevState) => ({
			...prevState,
			projectsDiff: newProjectDiffs,
		}));
		if (newProjectDiffs.remove.length > 0) {
			setRequiresRemovedProjectsMapping(true);
		} else {
			setRequiresRemovedProjectsMapping(false);
		}
		setDefaultSchemeMappingDiffs((prevState) => ({
			...prevState,
			projectsDiff: {
				...prevState.projectsDiff,
				add: newProjectDiffs.remove,
			},
		}));
	}, [pendingChanges]);

	useEffect(() => {
		setCurrentSchemeMappingDiffs((prevState) => ({
			...prevState,
			prioritiesDiff: calculatePrioritiesDiff(initialScheme.priorities, priorityScheme.priorities),
		}));
	}, [initialScheme.priorities, priorityScheme.priorities]);

	const currentSchemeMappingModalProps = usePriorityMappingsController({
		schemeId: priorityScheme.id ? priorityScheme.id : null,
		...currentSchemeMappingDiffs,
	});

	const defaultSchemeMappingModalProps = usePriorityMappingsController({
		schemeId: defaultSchemeId,
		...defaultSchemeMappingDiffs,
	});

	const onSave = async () => {
		if (!triggerSchemeDetailsValidation(priorityScheme)) {
			setModalStep('FormView');
			return;
		}

		setPrioritySchemeModalState(SAVING);

		try {
			const res = await savePrioritySchemeRequest(
				priorityScheme,
				prioritiesDiff,
				projectsDiff,
				mapPriorityMappingsToRequestMappings(
					currentSchemeMappingModalProps.mappings,
					defaultSchemeMappingModalProps.mappings,
				),
			);

			if (!res.isSuccess && 'errors' in res) {
				handleApiValidationErrors(res, setFormError);
				setPrioritySchemeModalState(DEFAULT);
				return;
			}

			if (!res.isSuccess) {
				showUnexpectedErrorFlag(isCreatingNewScheme);
				setPrioritySchemeModalState(DEFAULT);
				return;
			}

			const {
				data: { id: createdPrioritySchemeId },
			} = res;

			fireSaveAnalyticsEvent(
				createAnalyticsEvent,
				initialScheme,
				priorityScheme,
				createdPrioritySchemeId,
				projectsDiff,
			);

			const { data, isMigrationPending } = res;
			const flagData: FlagData = {
				schemeName: priorityScheme.name,
				schemeId: data.id,
			};

			if (isMigrationPending) {
				showAsyncSuccessFlag(flagData, isCreatingNewScheme);
			} else {
				showSuccessFlag(flagData, isCreatingNewScheme);
			}

			onComplete(ModalOnCompleteStatus.SUCCESS);
		} catch (error) {
			showUnexpectedErrorFlag(isCreatingNewScheme);
		}

		setPrioritySchemeModalState(DEFAULT);
	};

	const renderFormViewPrimaryButton = (): React.ReactElement => {
		let onSubmit: () => void = onSave;
		let isDisabled = false;

		let buttonContent: React.ReactNode = isCreatingNewScheme
			? formatMessage(messages.addPrioritySchemeButtonTitle)
			: formatMessage(messages.editPrioritySchemeButtonTitle);

		if (
			currentSchemeMappingModalProps.isLoading ||
			defaultSchemeMappingModalProps.isLoading ||
			prioritySchemeModalState === 'SAVING'
		) {
			buttonContent = <Spinner size="small" />;
			isDisabled = true;
		} else if (currentSchemeMappingModalProps.page.totalItems > 0) {
			onSubmit = () => setModalStep('AddedProjectsMapping');
			buttonContent = formatMessage(messages.mappingModalNextButtonTitle);
		} else if (defaultSchemeMappingModalProps.page.totalItems > 0) {
			onSubmit = () => setModalStep('RemovedProjectsMapping');
			buttonContent = formatMessage(messages.mappingModalNextButtonTitle);
		}

		return (
			<Button
				testId={PRIORITY_SCHEME_MODAL_SUBMIT_BUTTON_SELECTOR}
				appearance="primary"
				onClick={onSubmit}
				type="submit"
				isDisabled={isDisabled}
			>
				{buttonContent}
			</Button>
		);
	};

	const renderMappingInViewPrimaryButton = (): React.ReactElement => {
		let onSubmit: () => void = onSave;
		let isDisabled = false;

		let buttonContent: React.ReactNode = isCreatingNewScheme
			? formatMessage(messages.addPrioritySchemeButtonTitle)
			: formatMessage(messages.editPrioritySchemeButtonTitle);

		if (defaultSchemeMappingModalProps.isLoading || prioritySchemeModalState === 'SAVING') {
			buttonContent = <Spinner size="small" />;
			isDisabled = true;
		} else if (defaultSchemeMappingModalProps.page.totalItems > 0) {
			onSubmit = () => setModalStep('RemovedProjectsMapping');
			buttonContent = formatMessage(messages.mappingModalNextButtonTitle);
		}

		return (
			<Button
				testId={PRIORITY_SCHEME_MODAL_SUBMIT_BUTTON_SELECTOR}
				appearance="primary"
				onClick={onSubmit}
				type="submit"
				isDisabled={isDisabled}
			>
				{buttonContent}
			</Button>
		);
	};

	const renderMappingOutViewPrimaryButton = (): React.ReactElement => {
		const onSubmit: () => void = onSave;
		let isDisabled = false;

		let buttonContent: React.ReactNode = isCreatingNewScheme
			? formatMessage(messages.addPrioritySchemeButtonTitle)
			: formatMessage(messages.editPrioritySchemeButtonTitle);

		if (prioritySchemeModalState === 'SAVING') {
			buttonContent = <Spinner size="small" />;
			isDisabled = true;
		}

		return (
			<Button
				testId={PRIORITY_SCHEME_MODAL_SUBMIT_BUTTON_SELECTOR}
				appearance="primary"
				onClick={onSubmit}
				type="submit"
				isDisabled={isDisabled}
			>
				{buttonContent}
			</Button>
		);
	};

	const detailsFormProps = {
		formTab: prioritySchemeDetailsFormTab,
		formError,
		isCreatingNewScheme,
		onComplete,
		onSave,
		prioritySchemeModalState,
		projectPickerProps,
		...schemeDetailsProps,
		setFormTab: setPrioritySchemeDetailsFormTab,
		startingTab,
	};

	const renderModalContent = (): React.ReactElement => {
		if (modalStep === 'FormView') {
			return (
				<PrioritySchemeDetailsForm
					{...detailsFormProps}
					renderPrimaryButton={renderFormViewPrimaryButton}
					onRemovePriorityMapping={currentSchemeMappingModalProps.onRemovePriorityMapping}
				/>
			);
		}

		if (modalStep === 'AddedProjectsMapping') {
			return (
				<MappingModalView
					isDisabled={prioritySchemeModalState === 'SAVING'}
					modalTitle={formatMessage(messages.mappingModalTitle)}
					modalDescription={
						isCreatingNewScheme ? (
							<Stack space="space.025">
								{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
								<p>
									{formatMessage(messages.mappingModalAddSchemeDescription, {
										schemeName: priorityScheme.name,
									})}
								</p>
								<p>
									<FormattedMessage
										{...messages.mappingModalSelectNewPrioritiesDescription}
										values={{
											learnMoreLink: (
												<a href={learnMoreURL} target="_blank">
													{formatMessage(messages.mappingModalLearnMoreLink)}
												</a>
											),
										}}
									/>
								</p>
							</Stack>
						) : (
							<Stack space="space.025">
								<p>
									{formatMessage(messages.mappingModalEditSchemeDescription, {
										schemeName: priorityScheme.name,
									})}
								</p>
								<MappingInSchemesDescriptionUl>
									<li>{formatMessage(messages.mappingModalEditSchemeAddProjectsListItemOne)}</li>
								</MappingInSchemesDescriptionUl>
								<p>
									<FormattedMessage
										{...messages.mappingModalSelectNewPrioritiesDescription}
										values={{
											learnMoreLink: (
												<a href={learnMoreURL} target="_blank">
													{formatMessage(messages.mappingModalLearnMoreLink)}
												</a>
											),
										}}
									/>
								</p>
							</Stack>
						)
					}
					renderPrimaryButton={renderMappingInViewPrimaryButton}
					onPreviousStep={() => setModalStep('FormView')}
					mappablePriorities={priorityScheme.priorities}
					{...currentSchemeMappingModalProps}
				/>
			);
		}

		return (
			<MappingModalView
				isDisabled={prioritySchemeModalState === 'SAVING'}
				modalTitle={formatMessage(messages.mappingModalTitle)}
				modalDescription={
					<Stack space="space.025">
						<p>
							{formatMessage(messages.mappingModalEditSchemeDescription, {
								schemeName: priorityScheme.name,
							})}
						</p>
						<MappingInSchemesDescriptionUl>
							<li>
								{formatMessage(messages.mappingModalEditSchemeDescriptionRemoveProjectsListItemOne)}
							</li>
							<li>
								{formatMessage(messages.mappingModalEditSchemeDescriptionRemoveProjectsListItemTwo)}
							</li>
						</MappingInSchemesDescriptionUl>
						<p>
							<FormattedMessage
								{...messages.mappingModalSelectNewPrioritiesDescription}
								values={{
									learnMoreLink: (
										<a href={learnMoreURL} target="_blank">
											{formatMessage(messages.mappingModalLearnMoreLink)}
										</a>
									),
								}}
							/>
						</p>
					</Stack>
				}
				renderPrimaryButton={renderMappingOutViewPrimaryButton}
				onPreviousStep={() => {
					if (currentSchemeMappingModalProps.page.totalItems > 0) {
						setModalStep('AddedProjectsMapping');
					} else {
						setModalStep('FormView');
					}
				}}
				mappablePriorities={defaultSchemePriorities}
				{...defaultSchemeMappingModalProps}
				isError={defaultSchemeMappingModalProps.isError || isDefaultSchemeError}
				isLoading={isDefaultSchemeLoading}
			/>
		);
	};

	return (
		<PrioritySchemeModalWithAnalytics initialPriorityScheme={initialScheme}>
			{renderModalContent()}
		</PrioritySchemeModalWithAnalytics>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MappingInSchemesDescriptionUl = styled.ul({
	paddingLeft: token('space.400', '2rem'),
});
